import React from "react";
import { ChevronRight } from "lucide-react";
import { Localizer } from "@/main/react/Types";
import { getGroupManageIndexGroups, type GroupPageSelectOption } from "./GroupPageShared";
import { GroupTabCountBadge } from "./GroupTabCountBadge";

/**
 * `GROUP-82` (S7 U18, copy C62): the manage console's landing index.
 *
 * <p>The console already told a queue a leader owes a decision on ("Awaiting review") from a list that
 * merely has rows ("In this list"), and said so only in an `sr-only` span, while a leader tapping the
 * entry pill landed on Join requests because it was tab one. This is the page that pill now opens: the
 * tools the viewer has, grouped as "Needs you", "People" and "Settings", each row naming its queue size.
 * The "Needs you" count wears the bell's owed colour and the others the muted chip, so the distinction
 * is visible to sighted leaders too.
 *
 * <p>T5: counts are WORK (items in a queue), never a claim that people are waiting on the leader, and an
 * empty queue renders no pill at all (`GroupTabCountBadge` renders nothing for zero), never a `0`.
 *
 * <p>Rows are buttons handed to the console's own `onSelect`, like the strip's tools: the page layer
 * owns the push and the `metrics` hop to its own page.
 */
export function GroupManageConsoleIndex({
    tools,
    onSelect,
    localizer,
}: {
    tools: GroupPageSelectOption[];
    onSelect: (value: string) => void;
    localizer: Localizer;
}) {
    const groups = getGroupManageIndexGroups(tools, localizer);
    return (
        <div className="space-y-4" data-playwright-id="group-manage-index">
            {groups.map((group) => {
                const titleId = `group-manage-index-${group.key}-title`;
                return (
                    <section key={group.key} aria-labelledby={titleId} data-playwright-id={`group-manage-index-${group.key}`}>
                        <h3 id={titleId} className="mb-2 text-sm font-semibold text-text-main">{group.title}</h3>
                        <ul className="overflow-hidden rounded-xl border border-border bg-card">
                            {group.items.map((item) => {
                                const Icon = item.icon;
                                const hasCount = typeof item.badgeCount === "number" && item.badgeCount > 0;
                                return (
                                    <li key={item.key} className="border-b border-border last:border-b-0">
                                        <button
                                            type="button"
                                            onClick={() => onSelect(item.value)}
                                            data-playwright-id={`group-manage-index-item-${item.key}`}
                                            data-count-tone={hasCount ? group.tone : undefined}
                                            className="standard-hover flex min-h-11 w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-text-main focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50"
                                        >
                                            {Icon ? <Icon className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" /> : null}
                                            <span className="min-w-0 flex-1">{item.label}</span>
                                            <GroupTabCountBadge
                                                count={item.badgeCount}
                                                active={false}
                                                tone={group.tone}
                                                dataPlaywrightId={`group-manage-index-item-${item.key}-badge`}
                                            />
                                            {hasCount && item.badgeSrLabel ? <span className="sr-only">{item.badgeSrLabel}</span> : null}
                                            <ChevronRight className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    </section>
                );
            })}
        </div>
    );
}
